import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay, Bookmark, BookmarkBorder, Autorenew, Share } from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [forcingAiReply, setForcingAiReply] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleTogglePinned = async () => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				isPinned: !ticket.isPinned
			});
			ticket.isPinned = !ticket.isPinned;
			setLoading(false);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleForceAiReply = async () => {
		setForcingAiReply(true);
		try {
			const { data } = await api.put(`/tickets/${ticket.id}/force-ai-reply`);
			setForcingAiReply(false);
			if (data.sent) {
				toast.success("Resposta da IA enviada.");
			} else {
				toast.warning("A IA não gerou resposta desta vez (ver logs).");
			}
		} catch (err) {
			setForcingAiReply(false);
			toastError(err);
		}
	};

	const handleShareTicket = async () => {
		try {
			const { data } = await api.get(`/tickets/${ticket.id}/share-link`);
			const shareUrl = `${window.location.origin}/preview/${data.token}`;
			if (navigator.clipboard) {
				await navigator.clipboard.writeText(shareUrl);
				toast.success("📱 Link público do atendimento copiado com sucesso! 🚀");
			} else {
				window.open(shareUrl, "_blank");
			}
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons}>
			<IconButton
				onClick={handleShareTicket}
				title="Compartilhar atendimento (Gerar link público)"
				style={{ color: "#0284c7" }}
			>
				<Share />
			</IconButton>
			<IconButton
				onClick={handleTogglePinned}
				title={ticket.isPinned ? "Desafixar conversa" : "Fixar conversa"}
			>
				{ticket.isPinned ? <Bookmark color="primary" /> : <BookmarkBorder />}
			</IconButton>
			{ticket.whatsapp?.useAi && ticket.status === "pending" && (
				<IconButton
					onClick={handleForceAiReply}
					disabled={forcingAiReply}
					title="Forçar resposta da IA agora"
				>
					<Autorenew />
				</IconButton>
			)}
			{ticket.whatsapp?.useAi && ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="outlined"
					style={{
						textTransform: "none",
						fontWeight: "bold",
						borderColor: ticket.isAiPaused ? "#dc2626" : "#16a34a",
						color: ticket.isAiPaused ? "#dc2626" : "#16a34a",
						backgroundColor: ticket.isAiPaused ? "#fef2f2" : "#f0fdf4"
					}}
					onClick={async () => {
						setLoading(true);
						try {
							await api.put(`/tickets/${ticket.id}`, {
								isAiPaused: !ticket.isAiPaused
							});
							ticket.isAiPaused = !ticket.isAiPaused;
							setLoading(false);
						} catch (err) {
							setLoading(false);
							toastError(err);
						}
					}}
				>
					{ticket.isAiPaused ? "⏸️ IA Pausada (Reativar)" : "🤖 IA Ativa"}
				</ButtonWithSpinner>
			)}
			{ticket.status === "pending" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						🟢 Aceitar / Assumir Atendimento
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						style={{ backgroundColor: "#059669", color: "#ffffff", marginLeft: 8 }}
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						✅ Resolver
					</ButtonWithSpinner>
					{user?.profile === "admin" && (
						<>
							<IconButton onClick={handleOpenTicketOptionsMenu}>
								<MoreVert />
							</IconButton>
							<TicketOptionsMenu
								ticket={ticket}
								anchorEl={anchorEl}
								menuOpen={ticketOptionsMenuOpen}
								handleClose={handleCloseTicketOptionsMenu}
							/>
						</>
					)}
				</>
			)}
			{ticket.status === "closed" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.reopen")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "open" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
		</div>
	);
};

export default TicketActionButtons;
